/**
 * Preferências deste browser (barra lateral escondida, vista em grelha ou lista, último
 * espaço aberto). Só conveniências: sem acesso ao armazenamento (janela privada, dados
 * bloqueados), a app funciona com os valores por omissão.
 */
import type { MeResponse, WorkspaceSummary } from '@nolens/domain';
import { useCallback, useState, useSyncExternalStore } from 'react';

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Sem armazenamento: fica só nesta página.
  }
}

/** Um valor de uma lista fechada, guardado neste browser. */
export function usePref<T extends string>(key: string, values: readonly T[], fallback: T) {
  const [value, setValue] = useState<T>(() => {
    const v = read(key);
    return values.includes(v as T) ? (v as T) : fallback;
  });
  const set = useCallback(
    (v: T) => {
      setValue(v);
      write(key, v);
    },
    [key],
  );
  return [value, set] as const;
}

const DESKTOP = '(min-width: 768px)';

/** Ecrã largo (md): barra lateral fixa; abaixo disso, a barra lateral abre numa gaveta. */
export function useIsDesktop(): boolean {
  return useSyncExternalStore(
    (cb) => {
      const m = window.matchMedia(DESKTOP);
      m.addEventListener('change', cb);
      return () => m.removeEventListener('change', cb);
    },
    () => window.matchMedia(DESKTOP).matches,
  );
}

const SPACE_KEY = 'nolens:space';

/**
 * O espaço em contexto na barra lateral: o da página; sem ele (Início, conta), o último
 * aberto neste browser; senão a primeira equipa; senão o espaço pessoal.
 */
export function activeSpace(me: MeResponse, current?: string): WorkspaceSummary {
  const byId = (id: string | null | undefined) =>
    id ? me.workspaces.find((w) => w.id === id) : undefined;
  return (
    byId(current) ??
    byId(read(SPACE_KEY)) ??
    me.workspaces.find((w) => w.kind === 'team') ??
    byId(me.personalWorkspaceId) ??
    me.workspaces[0]!
  );
}

/** Guarda o espaço aberto, para a barra lateral o mostrar nas páginas sem espaço. */
export const rememberSpace = (id: string) => write(SPACE_KEY, id);
